/**
 * IA Builder — création manuelle d'étapes + énigmes pour un parcours.
 * Supporte les types classiques + VOYAGE_TEMPS (archive photo + GPS + rayon).
 */
import { useEffect, useRef, useState } from "react";
import { parcoursApi, iaBuilderApi } from "@/lib/api";
import { toast } from "sonner";
import {
  PageHeader,
  Card,
  Btn,
  Field,
  FieldLabel,
  Select,
  Textarea,
  EmptyState,
} from "@/components/DashboardUI";
import {
  Sparkles,
  KeyRound,
  Plus,
  MapPin,
  Upload,
  ImageOff,
  Compass,
  Clock,
} from "lucide-react";

const DEFAULT_ENIGME = {
  nom: "",
  emoji: "📍",
  lat: 44.6076,
  lng: 2.0349,
  contexte: "",
  codeCadenas: "1234",
  methodeCode: "DIRECT",
  resumeIdee: "",
  type: "TEXTE",
  question: "",
  reponse: "",
  indice1: "",
  indice2: "",
  indice3: "",
  explication: "",
  points: 120,
  malusErreur: 15,
  genereeIA: false,
  // Voyage dans le temps
  archivePhotoUrl: "",
  archiveAnnee: "",
  voyageLat: null,
  voyageLng: null,
  rayonMetres: 30,
};

const TYPES = [
  { key: "TEXTE", label: "Texte libre", icon: "📝" },
  { key: "QCM", label: "QCM (choix multiples)", icon: "🔍" },
  { key: "CALCUL", label: "Calcul", icon: "🧮" },
  { key: "OBSERVATION", label: "Observation", icon: "👁️" },
  { key: "VOYAGE_TEMPS", label: "Voyage dans le temps", icon: "🕰️" },
];

export default function IaBuilder() {
  const [parcoursList, setParcoursList] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [etat, setEtat] = useState(null);
  const [form, setForm] = useState(DEFAULT_ENIGME);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    parcoursApi.list().then(setParcoursList);
  }, []);

  useEffect(() => {
    if (selectedId) {
      iaBuilderApi
        .etat(selectedId)
        .then(setEtat)
        .catch(() => toast.error("Erreur chargement"));
    } else {
      setEtat(null);
    }
  }, [selectedId]);

  async function saveEnigme(e) {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = { ...form, parcoursId: selectedId };
      // Pour VOYAGE_TEMPS, la "réponse" est la localisation GPS — on met un placeholder
      if (form.type === "VOYAGE_TEMPS") {
        payload.reponse = payload.reponse || "GPS";
        // Défaut : point cible = étape GPS
        payload.voyageLat = form.voyageLat ?? form.lat;
        payload.voyageLng = form.voyageLng ?? form.lng;
      }
      await iaBuilderApi.saveComplete(payload);
      toast.success(`Étape "${form.nom}" + énigme créées`);
      setForm(DEFAULT_ENIGME);
      const newEtat = await iaBuilderApi.etat(selectedId);
      setEtat(newEtat);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="p-6 md:p-8 space-y-6" data-testid="iabuilder-page">
      <PageHeader
        chapter="Chapitre pro · L'atelier"
        title="Builder IA"
        subtitle="Composez le parcours étape par étape — génération Claude à venir."
        testId="iab-header"
      />

      <Card>
        <FieldLabel>Parcours à enrichir</FieldLabel>
        <div className="mt-2">
          <Select
            data-testid="select-parcours"
            value={selectedId}
            onChange={(e) => setSelectedId(e.target.value)}
          >
            <option value="" className="bg-noir">
              — Sélectionne un parcours —
            </option>
            {parcoursList.map((p) => (
              <option key={p.id} value={p.id} className="bg-noir">
                {p.nom} ({p.ville})
              </option>
            ))}
          </Select>
        </div>
      </Card>

      {etat && (
        <>
          {/* Stats du parcours */}
          <div className="grid grid-cols-3 gap-4">
            <StatChip
              label="Étapes créées"
              value={etat.stats.nbEtapesCreees}
              testid="stat-etapes"
            />
            <StatChip
              label="Énigmes créées"
              value={etat.stats.nbEnigmesCreees}
              testid="stat-enigmes"
            />
            <StatChip
              label="Cadenas restants"
              value={etat.stats.nbCadenasRestant}
              testid="stat-cadenas-restants"
              accent
            />
          </div>

          {/* Étapes existantes */}
          {etat.etapes.length > 0 && (
            <Card>
              <h2 className="font-display text-lg text-parchemin mb-4">
                Étapes existantes
              </h2>
              <ul className="space-y-2">
                {etat.etapes.map((e) => (
                  <li
                    key={e.id}
                    className="text-sm flex items-center justify-between p-3 rounded-sm bg-noir-100/60 border border-or/10"
                    data-testid={`etape-${e.id}`}
                  >
                    <div className="flex items-center gap-3">
                      <span className="w-7 h-7 rounded-full border border-or/30 bg-noir flex items-center justify-center font-display text-xs text-or">
                        {e.ordre}
                      </span>
                      <span className="text-lg leading-none">{e.emoji}</span>
                      <span className="text-parchemin">{e.nom}</span>
                    </div>
                    <span className="text-xs font-mono text-parchemin/50">
                      {e.enigmes.length > 0 ? (
                        <span>
                          Code :{" "}
                          <span className="text-or tracking-widest">
                            {e.enigmes[0].codeCadenas}
                          </span>
                        </span>
                      ) : (
                        <span className="italic">Pas d&apos;énigme</span>
                      )}
                    </span>
                  </li>
                ))}
              </ul>
            </Card>
          )}

          {/* Formulaire nouvelle étape */}
          <form onSubmit={saveEnigme} data-testid="enigme-form">
            <Card>
              <h2 className="flex items-center gap-2 font-display text-xl text-parchemin mb-6">
                <Sparkles className="w-4 h-4 text-or" strokeWidth={1.5} />
                Nouvelle étape + énigme
              </h2>

              {/* Type d'énigme */}
              <div className="flex flex-col gap-2 mb-5">
                <FieldLabel>Type d&apos;énigme</FieldLabel>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                  {TYPES.map((t) => (
                    <button
                      key={t.key}
                      type="button"
                      onClick={() => setForm({ ...form, type: t.key })}
                      data-testid={`type-${t.key.toLowerCase()}`}
                      className={`flex flex-col items-center gap-1 p-3 rounded-sm border-2 transition ${
                        form.type === t.key
                          ? t.key === "VOYAGE_TEMPS"
                            ? "border-or bg-gradient-to-br from-or/15 to-vert-900/40"
                            : "border-or bg-or/10"
                          : "border-or/15 bg-noir-100/40 hover:border-or/40"
                      }`}
                    >
                      <span className="text-2xl leading-none">{t.icon}</span>
                      <span className={`text-[10px] font-mono uppercase tracking-widest text-center leading-tight ${
                        form.type === t.key ? "text-or" : "text-parchemin/60"
                      }`}>
                        {t.label}
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Champs communs */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field
                  label="Nom de l'étape"
                  value={form.nom}
                  onChange={(v) => setForm({ ...form, nom: v })}
                  required
                  testid="form-en-nom"
                />
                <Field
                  label="Emoji"
                  value={form.emoji}
                  onChange={(v) => setForm({ ...form, emoji: v })}
                  testid="form-en-emoji"
                />
                <Field
                  label="Latitude"
                  type="number"
                  step="0.0001"
                  value={form.lat}
                  onChange={(v) => setForm({ ...form, lat: parseFloat(v) })}
                  required
                  testid="form-en-lat"
                />
                <Field
                  label="Longitude"
                  type="number"
                  step="0.0001"
                  value={form.lng}
                  onChange={(v) => setForm({ ...form, lng: parseFloat(v) })}
                  required
                  testid="form-en-lng"
                />
                <Field
                  label="Code cadenas (4 chiffres)"
                  value={form.codeCadenas}
                  onChange={(v) => setForm({ ...form, codeCadenas: v })}
                  required
                  testid="form-en-code"
                />
                <Field
                  label="Points"
                  type="number"
                  value={form.points}
                  onChange={(v) =>
                    setForm({ ...form, points: parseInt(v, 10) })
                  }
                  testid="form-en-points"
                />
              </div>

              <div className="mt-4 grid grid-cols-1 gap-4">
                <Field
                  label="Contexte (1 phrase)"
                  value={form.contexte}
                  onChange={(v) => setForm({ ...form, contexte: v })}
                  required
                  testid="form-en-contexte"
                />
                <Field
                  label="Question (ou consigne pour Voyage)"
                  value={form.question}
                  onChange={(v) => setForm({ ...form, question: v })}
                  required
                  testid="form-en-question"
                />
                {form.type !== "VOYAGE_TEMPS" && (
                  <Field
                    label="Réponse"
                    value={form.reponse}
                    onChange={(v) => setForm({ ...form, reponse: v })}
                    required
                    testid="form-en-reponse"
                  />
                )}
              </div>

              {/* Indices 3 niveaux */}
              <div className="mt-6 pt-6 border-t border-or/10">
                <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-or/70 mb-3">
                  Indices
                </div>
                <div className="grid grid-cols-1 gap-3">
                  <Field
                    label="Indice 1 (offert, amorce narrative)"
                    value={form.indice1}
                    onChange={(v) => setForm({ ...form, indice1: v })}
                    testid="form-en-ind1"
                  />
                  <Field
                    label="Indice 2 (-25 pts)"
                    value={form.indice2}
                    onChange={(v) => setForm({ ...form, indice2: v })}
                    testid="form-en-ind2"
                  />
                  <Field
                    label="Indice 3 (-50 pts)"
                    value={form.indice3}
                    onChange={(v) => setForm({ ...form, indice3: v })}
                    testid="form-en-ind3"
                  />
                </div>
              </div>

              {/* Anecdote */}
              <div className="mt-6 pt-6 border-t border-or/10 flex flex-col gap-2">
                <FieldLabel>Anecdote de révélation (« Le saviez-vous ? »)</FieldLabel>
                <Textarea
                  data-testid="form-en-explication"
                  value={form.explication}
                  onChange={(e) => setForm({ ...form, explication: e.target.value })}
                  rows={3}
                  placeholder="Ce qui a changé, l'histoire du lieu…"
                />
              </div>

              {/* Bloc VOYAGE_TEMPS */}
              {form.type === "VOYAGE_TEMPS" && (
                <VoyageBlock form={form} setForm={setForm} />
              )}

              <div className="mt-6 pt-6 border-t border-or/10">
                <Btn
                  type="submit"
                  disabled={loading || (form.type === "VOYAGE_TEMPS" && !form.archivePhotoUrl)}
                  size="lg"
                  data-testid="form-en-submit"
                >
                  {loading ? (
                    "Enregistrement…"
                  ) : (
                    <>
                      <Plus className="w-4 h-4" />
                      Ajouter étape + énigme
                    </>
                  )}
                </Btn>
              </div>
            </Card>
          </form>
        </>
      )}

      {!selectedId && (
        <EmptyState
          icon={<KeyRound className="w-6 h-6" />}
          title="Sélectionnez un parcours pour commencer à composer."
          hint="Vous verrez ensuite les étapes existantes et pourrez en ajouter de nouvelles."
          testId="iab-empty"
        />
      )}
    </div>
  );
}

function VoyageBlock({ form, setForm }) {
  const inputRef = useRef(null);

  async function onFileChange(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image trop lourde (>5 Mo).");
      return;
    }
    const b64 = await new Promise((resolve, reject) => {
      const r = new FileReader();
      r.onload = () => resolve(r.result);
      r.onerror = () => reject(new Error());
      r.readAsDataURL(file);
    });
    // Redimensionner à 1200px max côté long
    const resized = await new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        let { width, height } = img;
        const max = 1200;
        if (Math.max(width, height) > max) {
          const r = max / Math.max(width, height);
          width = Math.round(width * r);
          height = Math.round(height * r);
        }
        const c = document.createElement("canvas");
        c.width = width;
        c.height = height;
        c.getContext("2d").drawImage(img, 0, 0, width, height);
        resolve(c.toDataURL("image/jpeg", 0.85));
      };
      img.onerror = () => resolve(b64);
      img.src = b64;
    });
    setForm({ ...form, archivePhotoUrl: resized });
  }

  return (
    <div className="mt-6 pt-6 border-t border-or bg-gradient-to-br from-or/5 to-vert-900/20 -mx-5 md:-mx-6 px-5 md:px-6 pb-2">
      <div className="flex items-center gap-2 mb-4">
        <Clock className="w-4 h-4 text-or" strokeWidth={1.5} />
        <h3 className="font-display italic text-lg text-or">
          Voyage dans le temps · configuration
        </h3>
      </div>

      {/* Archive upload */}
      <div className="mb-5">
        <FieldLabel>Archive ancienne (photo d&apos;époque)</FieldLabel>
        {form.archivePhotoUrl ? (
          <div className="mt-2 relative border-2 border-or/40 rounded-sm overflow-hidden">
            <img
              src={form.archivePhotoUrl}
              alt="Archive"
              className="w-full h-auto max-h-64 object-cover"
              style={{ filter: "sepia(0.4) contrast(1.1)" }}
            />
            <div className="absolute top-2 right-2 flex gap-2">
              <button
                type="button"
                onClick={() => inputRef.current?.click()}
                className="px-2 py-1 rounded-sm bg-noir/80 text-or text-[10px] font-mono uppercase tracking-wider hover:bg-noir"
                data-testid="voyage-archive-replace"
              >
                <Upload className="w-3 h-3 inline mr-1" />
                Remplacer
              </button>
              <button
                type="button"
                onClick={() => setForm({ ...form, archivePhotoUrl: "" })}
                className="px-2 py-1 rounded-sm bg-oxyde/80 text-parchemin text-[10px] font-mono uppercase tracking-wider hover:bg-oxyde"
                data-testid="voyage-archive-remove"
              >
                Retirer
              </button>
            </div>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            data-testid="voyage-archive-upload"
            className="mt-2 w-full flex flex-col items-center justify-center gap-2 py-8 border-2 border-dashed border-or/40 hover:border-or hover:bg-or/5 rounded-sm text-or transition"
          >
            <ImageOff className="w-8 h-8 opacity-60" />
            <span className="font-mono text-xs tracking-widest uppercase">
              Choisir l&apos;archive
            </span>
            <span className="text-[10px] font-mono text-parchemin/50">
              JPEG / PNG · max 5 Mo · sera redimensionnée 1200px
            </span>
          </button>
        )}
        <input
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          className="hidden"
          onChange={onFileChange}
          data-testid="voyage-archive-input"
        />
      </div>

      {/* Métadonnées voyage */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Field
          label="Année de l'archive"
          value={form.archiveAnnee}
          onChange={(v) => setForm({ ...form, archiveAnnee: v })}
          placeholder="vers 1920"
          testid="voyage-annee"
        />
        <Field
          label="Rayon validation (m)"
          type="number"
          value={form.rayonMetres}
          onChange={(v) => setForm({ ...form, rayonMetres: parseInt(v, 10) || 30 })}
          testid="voyage-rayon"
        />
        <div className="flex flex-col gap-2">
          <FieldLabel>Point de vue GPS cible</FieldLabel>
          <div className="flex items-center gap-2 px-3 py-2.5 rounded-sm bg-noir-100 border border-or/20 text-parchemin/70 text-xs font-mono">
            <Compass className="w-3.5 h-3.5 text-or" />
            <span>
              {(form.voyageLat ?? form.lat).toFixed(4)},{" "}
              {(form.voyageLng ?? form.lng).toFixed(4)}
            </span>
          </div>
          <span className="text-[10px] font-mono text-parchemin/45">
            Par défaut : GPS de l&apos;étape.
          </span>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field
          label="Latitude cible (optionnel)"
          type="number"
          step="0.00001"
          value={form.voyageLat ?? ""}
          onChange={(v) => setForm({ ...form, voyageLat: v ? parseFloat(v) : null })}
          placeholder={String(form.lat)}
          testid="voyage-lat-cible"
        />
        <Field
          label="Longitude cible (optionnel)"
          type="number"
          step="0.00001"
          value={form.voyageLng ?? ""}
          onChange={(v) => setForm({ ...form, voyageLng: v ? parseFloat(v) : null })}
          placeholder={String(form.lng)}
          testid="voyage-lng-cible"
        />
      </div>

      <div className="mt-4 p-3 rounded-sm bg-or/5 border border-or/20 text-xs text-parchemin/70 flex gap-2">
        <Sparkles className="w-3.5 h-3.5 text-or flex-shrink-0 mt-0.5" />
        <span>
          Le joueur devra se rendre à moins de{" "}
          <span className="text-or font-mono">{form.rayonMetres || 30}m</span> du point cible,
          reprendre la photo, et Claude Vision confirmera le cadrage (le GPS reste
          la source de vérité).
        </span>
      </div>
    </div>
  );
}

function StatChip({ label, value, accent, testid }) {
  return (
    <div
      data-testid={testid}
      className={`border rounded-sm p-4 ${
        accent
          ? "border-vert-50/30 bg-vert-900/30"
          : "border-or/15 bg-noir-50/40"
      }`}
    >
      <div className="text-[10px] font-mono uppercase tracking-widest text-parchemin/60">
        {label}
      </div>
      <div className="font-display text-2xl text-parchemin stat-graved mt-1">
        {value}
      </div>
    </div>
  );
}
